/** @shell standard @header bar */
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { View, Text, StyleSheet, Image, Alert, Platform } from 'react-native';
import Animated from 'react-native-reanimated';
import { useRoute, useNavigation } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { SvgIcon } from '../components/common/SvgIcon';
import { ScreenHeader, useHeaderTopPadding, useScreenHeaderScroll } from '../components/common/ScreenHeader';
import { Clickable } from '../components/common/Clickable';
import { Input } from '../components/common/Input';
import { SolidCtaClickable } from '../components/common/SolidCtaClickable';
import { appTheme, useThemeColors, type ThemeColors } from '../shared/theme';
import { sharedStyles } from '../shared/theme/sharedStyles';
import { resolveImageUri } from '../shared/utils/resolveImageUri';
import { formatCurrency, formatCurrencyAmountForApi } from '../shared/utils/currencyUtils';
import { setBookMealData, placeOrder, validateCoupon } from '../data/domains/orders';
import { useApiConfig, resolveOrganizationPaymentMode } from '../data/domains/config';
/** Compact checkout line item (smaller than menu flow card; avoids corner clipping). */
const CHECKOUT_LINE_IMAGE = 64;
const CHECKOUT_LINE_IMAGE_SECTION = CHECKOUT_LINE_IMAGE + 8;
import { useAuthStore } from '../shared/auth/store';
import { useAddresses, useAreas } from '../data/domains/addresses';
import { LoadingState } from '../components/common/LoadingState';
import { useTranslation, getApiText } from '../shared/i18n';
import { useLoyalty } from '../data/domains/loyalty';
import { useLoyaltyFeatureEnabled } from '../data/domains/config';
import { useAuthNavigation, useExitOrGoBack } from '../shared/navigation/useAuthNavigation';
import { useRequireOnlineOrdersEnabled } from '../shared/navigation/useFeatureGateExit';
import {
  StackScreenShell,
  DASH_STUDIO_RADIUS,
  studioCardShell,
  androidScaleFont,
  androidFlatSurfaceStyle,
  STACK_SCROLL_GUTTER,
} from '../shared/theme/studio/stackScreenShell';

function createOrderCheckoutStyles(c: ThemeColors) {
  return StyleSheet.create({
    content: {
      flex: 1,
      backgroundColor: 'transparent',
    },
    scrollContent: {
      paddingHorizontal: STACK_SCROLL_GUTTER,
      backgroundColor: 'transparent',
    },
    heroCard: {
      paddingVertical: appTheme.spacing.lg,
      paddingHorizontal: appTheme.spacing.lg,
      marginBottom: appTheme.spacing.lg,
      ...studioCardShell(c),
      ...androidFlatSurfaceStyle(),
      backgroundColor: c.surface,
      borderRadius: DASH_STUDIO_RADIUS,
    },
    heroEyebrow: {
      fontSize: 11,
      fontWeight: '800',
      letterSpacing: 0.9,
      textTransform: 'uppercase',
      color: c.textSecondary,
      marginBottom: appTheme.spacing.xs,
    },
    heroTotal: {
      fontSize: androidScaleFont(30),
      fontWeight: '800',
      color: c.primary,
      letterSpacing: -0.8,
    },
    heroMeta: {
      ...appTheme.typography.bodySmall,
      color: c.textSecondary,
      marginTop: appTheme.spacing.sm,
    },
    sectionBlock: {
      marginBottom: appTheme.spacing.xl,
    },
    panel: {
      padding: appTheme.spacing.md,
      ...studioCardShell(c),
      ...androidFlatSurfaceStyle(),
      backgroundColor: c.surface,
      borderRadius: DASH_STUDIO_RADIUS,
    },
    panelHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: appTheme.spacing.sm,
      marginBottom: appTheme.spacing.md,
      paddingBottom: appTheme.spacing.sm,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: c.border,
    },
    panelTitle: {
      ...appTheme.typography.body,
      fontSize: 17,
      fontWeight: '700',
      color: c.text,
      flex: 1,
    },
    lineItemRow: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: appTheme.spacing.sm,
      paddingHorizontal: appTheme.spacing.xs,
    },
    lineItemDivider: {
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: c.border,
    },
    itemImageSection: {
      width: CHECKOUT_LINE_IMAGE_SECTION,
      justifyContent: 'center',
      alignItems: 'center',
    },
    itemImageInner: {
      width: CHECKOUT_LINE_IMAGE,
      height: CHECKOUT_LINE_IMAGE,
      borderRadius: 12,
      overflow: 'hidden',
      backgroundColor: c.borderLight,
    },
    itemImage: {
      width: '100%',
      height: '100%',
    },
    itemInfo: {
      flex: 1,
      minWidth: 0,
      paddingHorizontal: appTheme.spacing.sm,
      justifyContent: 'center',
    },
    itemName: {
      fontSize: 16,
      fontWeight: '600',
      color: c.text,
    },
    itemDesc: {
      ...appTheme.typography.caption,
      color: c.textSecondary,
      marginTop: 2,
    },
    itemPrice: {
      ...appTheme.typography.bodySmall,
      color: c.primary,
      fontWeight: '700',
      marginTop: appTheme.spacing.xs,
    },
    itemActions: {
      alignItems: 'flex-end',
      justifyContent: 'center',
      gap: appTheme.spacing.sm,
      paddingVertical: 4,
      paddingStart: appTheme.spacing.xs,
      minWidth: 72,
    },
    removeItemHit: {
      padding: appTheme.spacing.xs,
      borderRadius: appTheme.borderRadius.sm,
    },
    itemQty: {
      ...appTheme.typography.bodySmall,
      fontWeight: '700',
      color: c.textSecondary,
    },
    summaryHeadingRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: appTheme.spacing.sm,
      marginBottom: appTheme.spacing.md,
    },
    addressPickWrap: {
      marginBottom: appTheme.spacing.lg,
    },
    addressPickCard: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: c.surfaceLight ?? c.surfaceFade ?? `${c.text}08`,
      paddingVertical: appTheme.spacing.md,
      paddingHorizontal: appTheme.spacing.sm,
      borderRadius: DASH_STUDIO_RADIUS,
    },
    addressPickCardSelected: {
      backgroundColor: c.primaryUltra ?? `${c.primary}12`,
    },
    addressRadioOuter: {
      width: 22,
      height: 22,
      borderRadius: 11,
      borderWidth: 2,
      borderColor: c.border,
      marginEnd: appTheme.spacing.sm,
      alignItems: 'center',
      justifyContent: 'center',
    },
    addressRadioOuterSelected: { borderColor: c.primary },
    addressRadioInner: {
      width: 12,
      height: 12,
      borderRadius: 6,
      backgroundColor: c.primary,
    },
    addressPickInfo: { flex: 1, minWidth: 0, marginStart: appTheme.spacing.sm },
    addressPickName: { ...appTheme.typography.body, color: c.text, fontWeight: '600' },
    addressPickSub: { ...appTheme.typography.bodySmall, color: c.textSecondary, marginTop: 2 },
    addressAddNewCta: {
      marginTop: appTheme.spacing.xs,
    },
    couponRow: {
      flexDirection: 'row',
      alignItems: 'flex-end',
      gap: appTheme.spacing.sm,
    },
    couponInputWrap: {
      flex: 1,
      minWidth: 0,
    },
    loyaltyButtonsRow: {
      flexDirection: 'row',
      alignItems: 'stretch',
      gap: appTheme.spacing.md,
    },
    loyaltyButtonHalf: {
      flex: 1,
      minWidth: 0,
    },
    loyaltyCardInner: {
      marginTop: appTheme.spacing.xs,
    },
    loyaltyTopRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: appTheme.spacing.md,
      gap: appTheme.spacing.md,
    },
    loyaltyLabel: {
      ...appTheme.typography.bodySmall,
      color: c.textSecondary,
      flex: 1,
      minWidth: 0,
    },
    loyaltyPointsValue: {
      ...appTheme.typography.body,
      fontWeight: '700',
      color: c.text,
    },
    summaryPanel: {
      padding: appTheme.spacing.lg,
      ...studioCardShell(c),
      ...androidFlatSurfaceStyle(),
      backgroundColor: c.surface,
      borderRadius: DASH_STUDIO_RADIUS,
    },
    priceRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: appTheme.spacing.sm,
    },
    priceLabel: {
      ...appTheme.typography.bodySmall,
      color: c.textSecondary,
      flex: 1,
    },
    priceValue: {
      ...appTheme.typography.body,
      fontSize: 15,
      fontWeight: '600',
      color: c.text,
      textAlign: 'right',
    },
    discountText: {
      color: c.success,
    },
    priceDivider: {
      height: 1,
      backgroundColor: c.border,
      marginVertical: appTheme.spacing.md,
    },
    totalRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingTop: appTheme.spacing.sm,
    },
    totalLabel: {
      ...appTheme.typography.body,
      fontSize: 17,
      fontWeight: '700',
      color: c.text,
      flex: 1,
    },
    totalValue: {
      ...appTheme.typography.h3,
      fontSize: 20,
      fontWeight: '700',
      color: c.primary,
      textAlign: 'right',
    },
    termsSection: {
      marginTop: appTheme.spacing.sm,
      marginBottom: appTheme.spacing.md,
    },
    termsPanel: {
      padding: appTheme.spacing.md,
      ...studioCardShell(c),
      ...androidFlatSurfaceStyle(),
      backgroundColor: c.surface,
      borderRadius: DASH_STUDIO_RADIUS,
    },
    termsRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: appTheme.spacing.sm,
    },
    checkboxHit: {
      flexShrink: 0,
    },
    checkbox: {
      width: 22,
      height: 22,
      borderRadius: appTheme.borderRadius.xs,
      borderWidth: 2,
      borderColor: c.border,
      alignItems: 'center',
      justifyContent: 'center',
    },
    checkboxChecked: {
      backgroundColor: c.primary,
      borderColor: c.primary,
    },
    termsTextBlock: {
      flex: 1,
      minWidth: 0,
      flexDirection: 'row',
      flexWrap: 'wrap',
      alignItems: 'center',
    },
    termsText: {
      ...appTheme.typography.bodySmall,
      lineHeight: 20,
      color: c.textSecondary,
      ...(Platform.OS === 'android' ? { includeFontPadding: false } : {}),
    },
    termsLink: {
      ...appTheme.typography.bodySmall,
      lineHeight: 20,
      color: c.primary,
      fontWeight: '600',
      textDecorationLine: 'underline',
      ...(Platform.OS === 'android' ? { includeFontPadding: false } : {}),
    },
    footer: {
      padding: appTheme.spacing.md,
      backgroundColor: c.surface,
      borderTopWidth: 1,
      borderTopColor: c.border,
      ...appTheme.shadows.lg,
      shadowColor: c.shadowColor,
    },
  });
}

export const OrderCheckoutScreen: React.FC = () => {
  const navigation = useAuthNavigation();
  const online_orders_enabled = useRequireOnlineOrdersEnabled();
  const route = useRoute();
  const { t } = useTranslation();
  const themeColors = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => createOrderCheckoutStyles(themeColors), [themeColors]);
  const user = useAuthStore(s => s.user);
  const { organization } = useApiConfig();
  const params = (route.params as any) || {};
  const { items: routeItems = [], address: paramAddress } = params;
  const [lineItems, setLineItems] = useState<any[]>(routeItems);

  useEffect(() => {
    setLineItems(routeItems);
  }, [routeItems]);
  const { data: addressesData } = useAddresses();
  const { data: areasData } = useAreas();
  const addresses = Array.isArray(addressesData) ? addressesData : [];
  const areas = Array.isArray(areasData) ? areasData : [];
  const defaultAddress = addresses.find((a: any) => a.is_default) ?? addresses[0];
  const [selectedAddress, setSelectedAddress] = useState<any>(() => paramAddress ?? defaultAddress ?? null);

  useEffect(() => {
    if (paramAddress) {
      setSelectedAddress(paramAddress);
    }
  }, [paramAddress]);

  useEffect(() => {
    setSelectedAddress((prev: any) => (prev ? prev : defaultAddress ?? null));
  }, [defaultAddress]);

  const deliveryFee = useMemo(() => {
    if (!selectedAddress?.area_id) {return 0;}
    const area = areas.find((a: any) => a.id === selectedAddress.area_id || a.id === selectedAddress.area?.id);
    const charge = area?.order_delivery_charge ?? area?.delivery_charge ?? 0;
    return Number(charge) || 0;
  }, [selectedAddress?.area_id, selectedAddress?.area?.id, areas]);

  const payment_gateway_array = useMemo(
    () => (Array.isArray(organization?.payment_gateway) ? organization.payment_gateway : []),
    [organization?.payment_gateway],
  );
  const payment_mode = useMemo(
    () => resolveOrganizationPaymentMode(organization?.payment_mode),
    [organization?.payment_mode],
  );
  const payment_methods = useMemo(() => {
    return payment_gateway_array
      .map((gateway: { name?: string; display_name?: string }) => {
        const name = typeof gateway?.name === 'string' ? gateway.name.trim() : '';
        if (!name) {
          return null;
        }
        return {
          id: name,
          displayName: gateway.display_name || name,
        };
      })
      .filter((g): g is { id: string; displayName: string } => g != null);
  }, [payment_gateway_array]);
  const [selected_payment, set_selected_payment] = useState('');
  const can_pay_online = payment_mode !== 'manual' && payment_methods.length > 0;

  useEffect(() => {
    if (payment_methods.length === 0) {
      return;
    }
    const only_id = payment_methods.length === 1 ? payment_methods[0].id : null;
    if (!selected_payment) {
      set_selected_payment(payment_methods[0].id);
      return;
    }
    if (only_id != null && selected_payment !== only_id) {
      set_selected_payment(only_id);
    }
  }, [payment_methods, selected_payment]);

  const [isLoading, setIsLoading] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(false);
  const [discount, setDiscount] = useState(0);
  const [tcChecked, setTcChecked] = useState(false);
  const [redeemAmount, setRedeemAmount] = useState(0);

  const { data: loyaltySummary } = useLoyalty();
  const show_loyalty = useLoyaltyFeatureEnabled();
  const availablePoints = loyaltySummary?.count ?? loyaltySummary?.points ?? 0;

  const subtotal = lineItems.reduce((sum: number, item: any) => sum + item.price * (item.quantity || 1), 0);
  const totalBeforeRedeem = subtotal + deliveryFee - discount;
  const maxRedeem = Math.min(availablePoints, Math.max(0, totalBeforeRedeem));
  const effectiveRedeem = Math.min(redeemAmount, maxRedeem);
  const total = Math.max(0, totalBeforeRedeem - effectiveRedeem);

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {return;}
    try {
      const params: Record<string, string | number | undefined> = { coupon_code: couponCode.trim() };
      if (typeof user?.id === 'number') {params.customer_id = user.id;}
      const response: any = await validateCoupon(params);
      const data = response?.data ?? response;
      if (data?.valid) {
        setDiscount(Number(data.discount) || 0);
        setCouponApplied(true);
        Alert.alert(t('common.success'), t('order.couponAppliedSuccess'));
      } else {
        Alert.alert(t('checkout.invalidCoupon'), data?.message || t('order.couponInvalidMessage'));
      }
    } catch (error: any) {
      Alert.alert(t('common.error'), error?.message || t('order.failedToApplyCoupon'));
    }
  };

  const persistLineItems = useCallback((next: any[]) => {
    const stored = next.map((i: any) => ({
      id: i.id,
      quantity: i.quantity ?? 1,
      price: i.price,
      name: i.name,
      description: i.description,
      attachment: i.attachment ?? i.image,
      image: i.attachment ?? i.image,
      a_name: i.a_name,
      a_description: i.a_description,
    }));
    void setBookMealData(stored);
  }, []);

  const handleRemoveItem = useCallback(
    (itemId: number) => {
      Alert.alert(t('order.removeItemTitle'), t('order.removeItemMessage'), [
        { text: t('order.cancel'), style: 'cancel' },
        {
          text: t('order.remove'),
          style: 'destructive',
          onPress: () => {
            setLineItems(prev => {
              const next = prev.filter((i: any) => i.id !== itemId);
              persistLineItems(next);
              return next;
            });
          },
        },
      ]);
    },
    [t, persistLineItems],
  );

  const handlePlaceOrder = async () => {
    if (lineItems.length === 0) {
      return;
    }
    if (!tcChecked) {
      Alert.alert(t('checkout.termsRequired'), t('checkout.agreeToTermsRequired'));
      return;
    }
    if (!selectedAddress) {
      Alert.alert(t('common.error'), t('address.selectDeliveryAddress'));
      return;
    }
    if (typeof user?.id !== 'number') {
      Alert.alert(t('common.error'), t('order.failedToPlaceOrder'));
      return;
    }
    if (total > 0 && can_pay_online && !selected_payment) {
      Alert.alert(t('common.error'), t('checkout.selectPaymentType'));
      return;
    }
    if (total > 0 && !can_pay_online) {
      Alert.alert(t('common.error'), t('checkout.selectPaymentType'));
      return;
    }
    try {
      setIsLoading(true);
      const gateway =
        selected_payment ||
        payment_methods[0]?.id ||
        (typeof organization?.payment_gateway?.[0]?.name === 'string'
          ? organization.payment_gateway[0].name
          : '');
      if (total > 0 && !gateway) {
        Alert.alert(t('common.error'), t('checkout.selectPaymentType'));
        return;
      }
      const { chargeUrl, orderId, paymentSettled } = await placeOrder({
        customer_id: user.id,
        items: lineItems.map((item: any) => ({ id: item.id, quantity: item.quantity || 1, price: item.price })),
        address: { ...selectedAddress, area_id: selectedAddress.area_id ?? selectedAddress.area?.id },
        subtotal,
        delivery_cost: deliveryFee,
        discount,
        coupon_code: couponApplied ? couponCode : '',
        payment_gateway: gateway || 'knet',
        payment_type: 1,
        redeem_from_loyalty: effectiveRedeem,
        mail: user?.mail || (user as { email?: string }).email || selectedAddress?.mail || (selectedAddress as { email?: string })?.email,
      });
      if (chargeUrl) {
        navigation.navigate('PaymentWebView', { url: chargeUrl, orderId, flow: 'order' });
        return;
      }
      if (paymentSettled) {
        navigation.navigate('OrderConfirmed', {
          orderId: orderId != null ? String(orderId) : undefined,
          orderNumber: orderId != null ? String(orderId) : undefined,
          total: formatCurrencyAmountForApi(total),
        });
        return;
      }
      Alert.alert(t('common.error'), t('order.failedToPlaceOrder'));
    } catch (error: any) {
      Alert.alert(t('common.error'), error?.message || t('order.failedToPlaceOrder'));
    } finally {
      setIsLoading(false);
    }
  };

  const go_back = useExitOrGoBack();
  const inset = useSafeAreaInsets();
  const header_top = useHeaderTopPadding();
  const { scrollY, onScroll } = useScreenHeaderScroll();
  const content_padding_bottom = Math.max(inset.bottom, appTheme.spacing.lg) + 100;

  if (!online_orders_enabled) {
    return null;
  }

  if (isLoading) {
    return (
      <StackScreenShell colors={themeColors}>
        <View style={sharedStyles.container}>
          <ScreenHeader title={t('checkout.title')} onBack={go_back} scrollY={scrollY} />
          <LoadingState message={t('order.processingOrder')} />
        </View>
      </StackScreenShell>
    );
  }

  if (lineItems.length === 0) {
    return (
      <StackScreenShell colors={themeColors}>
        <View style={sharedStyles.container}>
          <ScreenHeader title={t('checkout.title')} onBack={go_back} scrollY={scrollY} />
          <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: appTheme.spacing.xl }}>
          <Text style={{ ...appTheme.typography.body, color: themeColors.textSecondary, textAlign: 'center', marginBottom: appTheme.spacing.lg }}>
            {t('order.noItemsInCart')}
          </Text>
          <View style={{ alignSelf: 'stretch', maxWidth: 400, width: '100%' }}>
            <SolidCtaClickable label={t('order.backToOrder')} onPress={() => navigation.navigate('MainTabs', { screen: 'Order' })} variant="primary" />
          </View>
        </View>
        </View>
      </StackScreenShell>
    );
  }

  return (
    <StackScreenShell colors={themeColors}>
    <View style={sharedStyles.container}>
      <ScreenHeader title={t('checkout.title')} onBack={go_back} scrollY={scrollY} />
      <Animated.ScrollView
        style={styles.content}
        onScroll={onScroll}
        scrollEventThrottle={16}
        contentContainerStyle={[styles.scrollContent, { paddingTop: header_top + appTheme.spacing.sm, paddingBottom: content_padding_bottom }]}
        showsVerticalScrollIndicator
        keyboardShouldPersistTaps="handled">
        <View style={styles.sectionBlock}>
          <View style={styles.heroCard}>
            <Text style={styles.heroEyebrow}>{t('order.total')}</Text>
            <Text style={styles.heroTotal}>{formatCurrency(total)}</Text>
            <Text style={styles.heroMeta}>
              {lineItems.length} {lineItems.length === 1 ? t('order.item') : t('order.items')}
            </Text>
          </View>
        </View>

        <View style={styles.sectionBlock}>
          <View style={styles.panel}>
            <View style={styles.panelHeader}>
              <SvgIcon name="package" size={22} color={themeColors.primary} />
              <Text style={styles.panelTitle}>{t('order.orderItems')}</Text>
            </View>
            {lineItems.map((item: any, index: number) => {
              const img_uri = resolveImageUri(item.attachment || item.image);
              const is_last = index === lineItems.length - 1;
              return (
                <View key={item.id || index} style={[styles.lineItemRow, !is_last && styles.lineItemDivider]}>
                  <View style={styles.itemImageSection}>
                    <View style={styles.itemImageInner}>
                      {img_uri ? (
                        <Image source={{ uri: img_uri }} style={styles.itemImage} resizeMode="cover" />
                      ) : (
                        <View style={[styles.itemImage, { justifyContent: 'center', alignItems: 'center', backgroundColor: themeColors.borderLight }]}>
                          <SvgIcon name="menu" size={24} color={themeColors.primary} />
                        </View>
                      )}
                    </View>
                  </View>
                  <View style={styles.itemInfo}>
                    <Text style={styles.itemName} numberOfLines={2}>
                      {getApiText(item as unknown as Record<string, unknown>, 'name', 'a_name') || item.name}
                    </Text>
                    {(() => {
                      const desc = getApiText(item as unknown as Record<string, unknown>, 'description', 'a_description') || item.description || '';
                      return desc ? (
                        <Text style={styles.itemDesc} numberOfLines={1}>
                          {desc}
                        </Text>
                      ) : null;
                    })()}
                    <Text style={styles.itemPrice}>{formatCurrency(item.price * (item.quantity || 1))}</Text>
                  </View>
                  <View style={styles.itemActions}>
                    <Text style={styles.itemQty}>×{item.quantity || 1}</Text>
                    <Clickable onPress={() => handleRemoveItem(item.id)} accessibilityLabel={t('order.remove')} accessibilityRole="button">
                      <View style={styles.removeItemHit}>
                        <SvgIcon name="trash" size={20} color={themeColors.error} />
                      </View>
                    </Clickable>
                  </View>
                </View>
              );
            })}
          </View>
        </View>

        <View style={styles.sectionBlock}>
          <View style={styles.panel}>
            <View style={styles.panelHeader}>
              <SvgIcon name="map" size={22} color={themeColors.primary} />
              <Text style={styles.panelTitle}>{t('order.selectDeliveryAddress')}</Text>
            </View>
            {addresses.length === 0 ? (
              <SolidCtaClickable
                label={t('address.addAddress')}
                onPress={() => navigation.navigate('AddressForm')}
                variant="primary"
                accessibilityLabel={t('address.selectDeliveryAddress')}
              />
            ) : (
              <>
                {addresses.map((addr: any) => {
                  const is_selected = selectedAddress?.id === addr.id;
                  return (
                    <Clickable
                      key={addr.id}
                      style={styles.addressPickWrap}
                      onPress={() => setSelectedAddress(addr)}
                      accessibilityLabel={getApiText(addr, 'name', 'name_ar') || addr.name || t('address.addressFallback')}
                      accessibilityRole="button">
                      <View style={[styles.addressPickCard, is_selected && styles.addressPickCardSelected]}>
                        <View style={[styles.addressRadioOuter, is_selected && styles.addressRadioOuterSelected]}>
                          {is_selected ? <View style={styles.addressRadioInner} /> : null}
                        </View>
                        <View style={styles.addressPickInfo}>
                          <Text style={styles.addressPickName}>{getApiText(addr, 'name', 'name_ar') || addr.name || t('address.addressFallback')}</Text>
                          <Text style={styles.addressPickSub} numberOfLines={2}>
                            {addr.address || addr.full_address || addr.block || '—'}
                          </Text>
                        </View>
                      </View>
                    </Clickable>
                  );
                })}
                <View style={styles.addressAddNewCta}>
                  <SolidCtaClickable
                    label={t('address.addNew')}
                    onPress={() => navigation.navigate('AddressForm')}
                    variant="primary"
                    size="compact"
                    accessibilityLabel={t('address.addNew')}
                  />
                </View>
              </>
            )}
          </View>
        </View>

        <View style={styles.sectionBlock}>
          <View style={styles.panel}>
            <View style={styles.panelHeader}>
              <SvgIcon name="tag" size={22} color={themeColors.primary} />
              <Text style={styles.panelTitle}>{t('checkout.couponCode')}</Text>
            </View>
            <View style={styles.couponRow}>
              <View style={styles.couponInputWrap}>
                <Input
                  placeholder={t('checkout.enterCouponPlaceholder')}
                  value={couponCode}
                  onChangeText={setCouponCode}
                  editable={!couponApplied}
                  containerStyle={{ marginBottom: 0 }}
                />
              </View>
              <SolidCtaClickable
                label={couponApplied ? t('order.remove') : t('checkout.applyCoupon')}
                onPress={
                  couponApplied
                    ? () => {
                        setCouponApplied(false);
                        setDiscount(0);
                        setCouponCode('');
                      }
                    : handleApplyCoupon
                }
                variant={couponApplied ? 'outline' : 'primary'}
                size="compact"
                widthMode="hug"
                accessibilityLabel={couponApplied ? t('order.remove') : t('checkout.applyCoupon')}
              />
            </View>
          </View>
        </View>

        {show_loyalty && availablePoints > 0 && totalBeforeRedeem > 0 && (
          <View style={styles.sectionBlock}>
            <View style={styles.panel}>
              <View style={styles.panelHeader}>
                <SvgIcon name="star" size={22} color={themeColors.primary} />
                <Text style={styles.panelTitle}>{t('order.redeemFromLoyalty')}</Text>
              </View>
              <View style={styles.loyaltyCardInner}>
                <View style={styles.loyaltyTopRow}>
                  <Text style={styles.loyaltyLabel}>{t('order.loyaltyPointsAvailable')}</Text>
                  <Text style={styles.loyaltyPointsValue}>{availablePoints}</Text>
                </View>
                <View style={styles.loyaltyButtonsRow}>
                  <View
                    style={[
                      styles.loyaltyButtonHalf,
                      maxRedeem <= 0 || effectiveRedeem >= maxRedeem ? { opacity: 0.5 } : null,
                    ]}>
                    <SolidCtaClickable
                      label={t('order.useMax')}
                      onPress={() => setRedeemAmount(maxRedeem)}
                      disabled={maxRedeem <= 0 || effectiveRedeem >= maxRedeem}
                      variant="primary"
                      size="compact"
                      accessibilityLabel={t('order.useMax')}
                    />
                  </View>
                  {effectiveRedeem > 0 ? (
                    <View style={styles.loyaltyButtonHalf}>
                      <SolidCtaClickable
                        label={t('order.clear')}
                        onPress={() => setRedeemAmount(0)}
                        variant="outline"
                        size="compact"
                        accessibilityLabel={t('order.clear')}
                      />
                    </View>
                  ) : null}
                </View>
              </View>
            </View>
          </View>
        )}

        <View style={styles.sectionBlock}>
          <View style={styles.summaryHeadingRow}>
            <SvgIcon name="receipt" size={22} color={themeColors.primary} />
            <Text style={styles.panelTitle}>{t('order.orderSummary')}</Text>
          </View>
          <View style={styles.summaryPanel}>
            <View style={styles.priceRow}>
              <Text style={styles.priceLabel}>{t('order.subtotal')}</Text>
              <Text style={styles.priceValue}>{formatCurrency(subtotal)}</Text>
            </View>
            {deliveryFee > 0 && (
              <View style={styles.priceRow}>
                <Text style={styles.priceLabel}>{t('order.deliveryFee')}</Text>
                <Text style={styles.priceValue}>{formatCurrency(deliveryFee)}</Text>
              </View>
            )}
            {discount > 0 && (
              <View style={styles.priceRow}>
                <Text style={styles.priceLabel}>{t('order.discount')}</Text>
                <Text style={[styles.priceValue, styles.discountText]}>-{formatCurrency(discount)}</Text>
              </View>
            )}
            {effectiveRedeem > 0 && (
              <View style={styles.priceRow}>
                <Text style={styles.priceLabel}>{t('order.loyaltyRedeem')}</Text>
                <Text style={[styles.priceValue, styles.discountText]}>-{formatCurrency(effectiveRedeem)}</Text>
              </View>
            )}
            <View style={styles.priceDivider} />
            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>{t('order.total')}</Text>
              <Text style={styles.totalValue}>{formatCurrency(total)}</Text>
            </View>
          </View>
        </View>

        {can_pay_online && payment_methods.length > 1 ? (
          <View style={styles.sectionBlock}>
            <View style={styles.panel}>
              <View style={styles.panelHeader}>
                <SvgIcon name="wallet" size={18} color={themeColors.primary} />
                <Text style={styles.panelTitle}>{t('checkout.selectPaymentType')}</Text>
              </View>
              {payment_methods.map(method => {
                const selected = selected_payment === method.id;
                return (
                  <Clickable
                    key={method.id}
                    onPress={() => set_selected_payment(method.id)}
                    accessibilityRole="radio"
                    accessibilityState={{ selected }}
                    accessibilityLabel={method.displayName}
                    style={[styles.lineItemRow, styles.lineItemDivider]}>
                    <View style={[styles.checkbox, selected && styles.checkboxChecked]}>
                      {selected ? <SvgIcon name="check" size={14} color={themeColors.onPrimary} /> : null}
                    </View>
                    <Text style={[styles.panelTitle, { marginBottom: 0, marginLeft: appTheme.spacing.sm }]}>
                      {method.displayName}
                    </Text>
                  </Clickable>
                );
              })}
            </View>
          </View>
        ) : null}

        <View style={[styles.sectionBlock, styles.termsSection]}>
          <View style={styles.termsPanel}>
            <View style={styles.termsRow}>
              <Clickable
                onPress={() => setTcChecked(!tcChecked)}
                accessibilityLabel={t('auth.agreeToTerms')}
                accessibilityRole="checkbox"
                accessibilityState={{ checked: tcChecked }}
                hitSlop={appTheme.hitSlop.small}>
                <View style={styles.checkboxHit}>
                  <View style={[styles.checkbox, tcChecked && styles.checkboxChecked]}>
                    {tcChecked ? <SvgIcon name="check" size={14} color={themeColors.onPrimary} /> : null}
                  </View>
                </View>
              </Clickable>
              <View style={styles.termsTextBlock}>
                <Text style={styles.termsText}>{t('auth.agreeToTerms')} </Text>
                <Clickable
                  onPress={() => navigation.navigate('TermsAndConditions')}
                  accessibilityRole="link"
                  accessibilityLabel={t('auth.termsAndConditions')}
                  hitSlop={appTheme.hitSlop.small}>
                  <Text style={styles.termsLink}>{t('auth.termsAndConditions')}</Text>
                </Clickable>
              </View>
            </View>
          </View>
        </View>
      </Animated.ScrollView>

      <View style={[styles.footer, { paddingBottom: Math.max(inset.bottom, appTheme.spacing.md) }]}>
        <View style={!tcChecked || !selectedAddress || lineItems.length === 0 ? { opacity: 0.5 } : undefined}>
          <SolidCtaClickable
            label={`${t('checkout.placeOrder')} · ${formatCurrency(total)}`}
            onPress={handlePlaceOrder}
            disabled={!tcChecked || !selectedAddress || lineItems.length === 0}
            variant="primary"
            haptics={true}
            accessibilityLabel={t('checkout.placeOrder')}
          />
        </View>
      </View>
    </View>
    </StackScreenShell>
  );
};
